import { ChangeEvent, useCallback, useState } from 'react';

import { Button, Input, Typography } from '@braskai/ui-kit';

import { Stack } from '@mui/material';
import { z } from 'zod';

import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';

import { Member } from '../lib/types';

const emailSchema = z.string().email('Incorrect email');

type MemberInviteProps = {
  isDisabled?: boolean;
  isLoading?: boolean;
  members: Member[];
  onInvite: (email: string) => void;
};

export const MemberInvite = ({ isDisabled = false, isLoading = false, members, onInvite }: MemberInviteProps) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<null | string>(null);

  const validateEmail = useCallback(
    (value: string) => {
      if (!value) {
        setError(null);
        return false;
      }

      try {
        emailSchema.parse(value);

        const isDuplicateEmail = members.some((member) => member.email === value);

        if (isDuplicateEmail) {
          setError('This email is already added');
          return false;
        }

        setError(null);
        return true;
      } catch (err) {
        if (err instanceof z.ZodError) {
          setError('Incorrect email');
        }

        return false;
      }
    },
    [members],
  );

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (!value) {
      validateEmail(value);
    }

    setEmail(value);
  };

  const handleInvite = () => {
    if (validateEmail(email)) {
      onInvite(email);
      setEmail('');
      setError(null);
      MixpanelEvents.inviteMemberSpace();
    }
  };

  return (
    <Stack gap={8}>
      <Typography color="grey.700" variant="body-2-md">
        Members
      </Typography>
      <Stack direction="row" gap={8}>
        <Input
          data-testid="teamspace-invite-member-input"
          disabled={isDisabled}
          placeholder="Enter email"
          value={email}
          onChange={handleEmailChange}
        />
        <Button
          sx={{
            flexShrink: 0,
            width: '74px',
          }}
          data-testid="teamspace-invite-member-button"
          disabled={!email || isDisabled || isLoading}
          isLoading={isLoading}
          type="button"
          variant="outlined"
          onClick={handleInvite}
        >
          Invite
        </Button>
      </Stack>
      {error && (
        <Typography color="error.500" variant="body-2">
          {error}
        </Typography>
      )}
    </Stack>
  );
};
